import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { adminWhoami } from "@/lib/api";
import { Button } from "@/components/ui/button";
import WeaveLogo from "@/components/WeaveLogo";
import { Compass, Layers, Calendar, MessageCircle, ClipboardCheck, Home, LogOut, Shield } from "lucide-react";

const NAV = [
  { to: "/app", end: true, label: "Overview", icon: Home },
  { to: "/app/foundations", label: "Foundations", icon: Compass },
  { to: "/app/pillars", label: "Pillars", icon: Layers },
  { to: "/app/calendar", label: "Calendar", icon: Calendar },
  { to: "/app/coach", label: "Coach", icon: MessageCircle },
  { to: "/app/audits", label: "Audits", icon: ClipboardCheck },
];

export default function MemberShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  // Quietly probe whether this user is on the admin allowlist so we can
  // surface the Admin nav link. 403 = not admin, silently hide.
  useEffect(() => {
    if (!user) return;
    adminWhoami()
      .then(() => setIsAdmin(true))
      .catch(() => setIsAdmin(false));
  }, [user]);

  if (loading || !user) {
    return (
      <div className="section-pad py-32">
        <div className="overline">Loading…</div>
      </div>
    );
  }

  return (
    <div data-testid="member-shell" className="min-h-screen flex bg-[var(--bg)]">
      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-30 w-72 bg-[var(--paper)] border-r border-[var(--line)] transform ${
          open ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 transition-transform`}
      >
        <div className="p-6 flex items-center justify-between">
          <NavLink to="/" className="flex items-center">
            <WeaveLogo height={26} />
          </NavLink>
          <button
            onClick={() => setOpen(false)}
            className="md:hidden text-sm text-[var(--muted)]"
            data-testid="sidebar-close"
          >
            close
          </button>
        </div>
        <div className="px-6 mb-3">
          <div className="overline">The Workspace</div>
        </div>
        <nav className="px-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition ${
                  isActive
                    ? "bg-[var(--bg-2)] text-[var(--ink)]"
                    : "text-[var(--ink-2)] hover:bg-[var(--bg-2)]"
                }`
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/app/admin"
              onClick={() => setOpen(false)}
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition ${
                  isActive
                    ? "bg-[var(--bg-2)] text-[var(--ink)]"
                    : "text-[var(--ink-2)] hover:bg-[var(--bg-2)]"
                }`
              }
            >
              <Shield size={16} />
              Admin
            </NavLink>
          )}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-[var(--line)] bg-[var(--paper)]">
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-9 h-9 rounded-full object-cover"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[var(--teal)] text-white flex items-center justify-center font-display">
                {(user.name || "F").charAt(0)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm tracking-tight truncate">
                {user.name}
              </div>
              <div className="text-xs text-[var(--muted)] truncate">{user.email}</div>
            </div>
            <button
              onClick={logout}
              data-testid="signout-btn"
              title="Sign out"
              className="text-[var(--muted)] hover:text-[var(--red)]"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile open button */}
      <button
        onClick={() => setOpen(true)}
        className="md:hidden fixed top-4 left-4 z-40 px-3 py-2 bg-[var(--ink)] text-white rounded-full text-xs"
        data-testid="sidebar-open"
      >
        Menu
      </button>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <div className="px-6 md:px-12 lg:px-16 py-10 md:py-14 max-w-6xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
